import React from 'react';
import CustomScroll from 'react-custom-scroll';
import { calculateGrossPrice, IServiceDetails } from '../../entities/service';
import PrimaryButton from '../form-components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../form-components/SecondaryButton/SecondaryButton';
import { IPopupable } from '../Popup/Popup';
import globalMessages from 'src/services/globalMessages';
import PersonCount from '../BookingFlow/modules/DateTimeSelect/PersonCount/PersonCount';
import { useState } from 'react';
import FormattedPrice from '../BookingFlow/modules/ServiceSelect/FormattedPrice/FormattedPrice';
import { useStyleLinks } from 'src/hooks';
import { useRef } from 'react';
import { FormattedMessage, FormattedDate, MessageDescriptor } from 'react-intl';
import { ISchedule } from 'src/entities/schedule';
import Image from '../Image/Image';

interface IServiceDescriptionProps extends IPopupable {
    service: IServiceDetails;
    schedule?: ISchedule;
    startReservation?: (freeSeatCount?: number) => void;
    notAvailableText?: MessageDescriptor;
    needsPayment?: boolean;
}

const ServiceDescription: React.FunctionComponent<IServiceDescriptionProps> = props => {
    const { IsDurationVisible, DurationToUser, Description, ShortDescription, DisplayedPrice, DisplayedCurrency, Price, Currency, Note, PictureLink, VatPercentage: Vat, PaymentMethod } = props.service;
    const { startReservation, closePopup, schedule, notAvailableText, needsPayment } = props;
    const freeSeatCount = props.schedule?.FreeSeatsCount;
    const reservationLimit = props.schedule?.ReservationLimit;

    const handleOk = () => {
        closePopup?.();
        if (startReservation) { startReservation(personCount); }
    }

    const bodyRef = useRef<HTMLDivElement>(null);
    useStyleLinks(bodyRef);

    const [personCount, setPersonCount] = useState(1);

    return (
        <section className="service-description">
            <CustomScroll>
                <div className="service-description-scroll">
                    <div className="service-description__details">
                        {schedule?.Start &&
                            <time dateTime={schedule.Start} className="service-description__detail">
                                <FormattedDate
                                    value={schedule.Start}
                                    year="numeric"
                                    month="numeric"
                                    day="numeric"
                                    hour="numeric"
                                    minute="numeric" />
                            </time>
                        }
                        {IsDurationVisible &&
                            <span className="service-description__detail">
                                <FormattedMessage {...globalMessages.minute} values={{ n: DurationToUser }} />
                            </span>
                        }
                        {DisplayedPrice ?
                            <FormattedPrice className="service-description__detail" value={DisplayedPrice} currency={DisplayedCurrency} />
                            : <></>
                        }
                        {needsPayment &&
                            <div className="service-description__price">
                                <span className="service-description__online-payable">
                                    <FormattedMessage {...globalMessages.toBePaid} />
                                </span>
                                <span className="service-description__online-price">
                                    <FormattedPrice value={calculateGrossPrice({ Price, Currency, PaymentMethod, VatPercentage: Vat })} currency={Currency} />
                                </span>
                            </div>
                        }
                    </div>
                    {schedule?.Note &&
                        <div className="service-description-note service-description-note--schedule">{schedule.Note}</div>
                    }
                    {ShortDescription &&
                        <div className="service-description__body">{ShortDescription}</div>
                    }
                    {Note &&
                        <div className="service-description-note">{Note}</div>
                    }
                    {PictureLink &&
                        <Image className="service-description__service-logo" src={PictureLink} />
                    }
                    {Description &&
                        <div ref={bodyRef} className="service-description__body" dangerouslySetInnerHTML={{ __html: Description }} />
                    }
                </div>
            </CustomScroll>
            {startReservation &&
                <div className="service-description__buttons">
                    {closePopup && freeSeatCount === undefined &&
                        <SecondaryButton
                            className="service-description__button service-description__cancel"
                            onClick={closePopup}
                            caption={globalMessages.close} />
                    }
                    {!!reservationLimit &&
                        <div className="service-description__person-count">
                            <PersonCount count={personCount} maxCount={reservationLimit} onChange={setPersonCount} />
                        </div>
                    }
                    {(freeSeatCount === undefined || freeSeatCount > 0) &&
                        <PrimaryButton
                            autoFocus={true}
                            className="service-description__button"
                            onClick={handleOk} caption={globalMessages.serviceDescriptionStartReservation} />
                    }
                    {freeSeatCount === 0 &&
                        <FormattedMessage {...globalMessages.full} />
                    }
                </div>
            }
            {notAvailableText &&
                <div className="service-description__not-available">
                    <FormattedMessage {...notAvailableText} />
                </div>
            }
        </section>
    );
}

export default ServiceDescription;